/* Constants for the server-side multi-cost-center Actuals export (S&M only). */
import { BUSINESS_GROUP_CODES } from './business-group-constants';

/** The only business group the export API accepts. */
export const ACTUALS_EXPORT_BUSINESS_GROUP = BUSINESS_GROUP_CODES.SALES_AND_MARKETING;

/** SSM parameter holding the export API base URL (no trailing slash). */
export const ACTUALS_EXPORT_API_URL_PARAMETER = '/xpt-fe/actuals-export-api-url';

/**
 * The ID token is sent to this base URL, so only an https `export-api.` host of the xPT domain is
 * accepted (for example `https://export-api.alpha.xpt.device.finance.amazon.dev`).
 */
export const ACTUALS_EXPORT_API_BASE_URL_PATTERN = /^https:\/\/export-api\.([a-z0-9-]+\.)*xpt\.device\.finance\.amazon\.dev(\/[A-Za-z0-9._~-]+)*\/?$/;

/** Server-generated export ID: `{yyyymmddTHHMMSSZ}-{uuid4 hex}`. Checked before it is put in a path. */
export const ACTUALS_EXPORT_ID_PATTERN = /^\d{8}T\d{6}Z-[0-9a-f]{32}$/;

/** The API accepts 1 to 30 cost centers per export. */
export const ACTUALS_EXPORT_MAX_COST_CENTERS = 30;

/** The API accepts at most 3 sort entries. */
export const ACTUALS_EXPORT_MAX_SORT_ENTRIES = 3;

/** BroadcastChannel shared by the xPT tabs of one browser. */
export const ACTUALS_EXPORT_CHANNEL_NAME = 'xpt-actuals-export';

/** A leader heartbeat older than this is ignored, so another tab can take over. */
export const ACTUALS_EXPORT_LEADER_TTL_MS = 15_000;

/** How often the leader renews its heartbeat and followers check for a free leader slot. */
export const ACTUALS_EXPORT_HEARTBEAT_INTERVAL_MS = 5_000;

/**
 * A fresh leader claim is confirmed by re-reading the slot after a randomized delay in
 * [MIN, MIN + JITTER) ms, so two tabs claiming the same free slot resolve to one leader.
 */
export const ACTUALS_EXPORT_CLAIM_CONFIRM_MIN_MS = 50;
export const ACTUALS_EXPORT_CLAIM_CONFIRM_JITTER_MS = 100;

/** Poll every 5 s for the first minute after the request, then every 10 s. */
export const ACTUALS_EXPORT_FAST_POLL_INTERVAL_MS = 5_000;
export const ACTUALS_EXPORT_SLOW_POLL_INTERVAL_MS = 10_000;
export const ACTUALS_EXPORT_FAST_POLL_WINDOW_MS = 60_000;

/** App-level flash message IDs, so a later state replaces the earlier message. */
export const ACTUALS_EXPORT_PROGRESS_FLASH_ID = 'actuals-export-progress';
export const ACTUALS_EXPORT_RESULT_FLASH_ID = 'actuals-export-result';

/** Alpha account ID, used for the exports bucket only when a local build has no account ID. */
export const ACTUALS_EXPORT_ALPHA_ACCOUNT_ID = '631849255022';

export const ACTUALS_EXPORT_MESSAGES = {
  BUTTON: 'Export Multiple Cost Centers',
  MODAL_HEADER: 'Export Multiple Cost Centers',
  MODAL_DESCRIPTION: "The file is built on our servers. You can keep using xPT; we'll download it when it's ready.",
  CLASSIFICATION_NOTICE: 'Contains Amazon Confidential financial data and personal data (PO requestor).',
  UNSUPPORTED_GRID_STATE_NOTICE: 'Filters, grouping and pivot are not applied to multi-cost-center exports.',
  COST_CENTER_LABEL: 'Cost centers',
  COST_CENTER_DESCRIPTION: 'Choose the cost centers to include in one Excel file.',
  COST_CENTER_EMPTY: 'No cost centers',
  EXPORT: 'Export',
  CANCEL: 'Cancel',
  DOWNLOAD_AGAIN: 'Download again',
  RETRY: 'Retry',
  NO_SELECTION: 'Choose at least one cost center.',
  TOO_MANY_SELECTED: (max: number): string => `Choose up to ${max} cost centers.`,
  IN_PROGRESS: (startedAt: string): string => (startedAt ? `An export started at ${startedAt} is still running.` : 'An export is still running.'),
  PREPARING_HEADER: (costCenterLabel: string): string => `Preparing Excel for ${costCenterLabel}`,
  PREPARING_CONTENT: "You can keep using xPT. We'll download the file when it's ready.",
  READY_HEADER: (costCenterLabel: string): string => `Excel for ${costCenterLabel} is ready`,
  READY_CONTENT: (fileName: string | null): string => (fileName ? `Downloaded ${fileName}.` : 'Downloaded the file.'),
  READY_IN_OTHER_TAB_CONTENT: 'Downloaded the file in another xPT tab.',
  ABORT: 'Abort',
  ABORTED_HEADER: 'Export aborted',
  ABORTED_CONTENT: 'You can start a new export with a different cost center selection.',
  FAILED_HEADER: 'Excel export failed',
  DOWNLOAD_FAILED_HEADER: 'Excel download failed',
  DOWNLOAD_URL_REJECTED: 'The download link did not point to the xPT exports location, so we did not open it. Contact the xPT team.'
} as const;
